import * as THREE from 'three';
import { ATLAS_GRID } from './atlas.js';

/**
 * ===========================================================================
 *  GPU PARTICLE POOL
 * ===========================================================================
 *
 * One draw call per blend mode. Every particle is one instance of a unit quad
 * in an InstancedBufferGeometry; the whole trajectory is evaluated in the
 * vertex shader from a handful of static per-instance attributes, so the CPU
 * touches a particle exactly once — when it is born — and never again.
 *
 * Motion is the closed-form solution of  v' = -k*v + g  (linear drag plus
 * gravity), which is exact, stable at any frame rate and costs one exp():
 *
 *      s   = (1 - e^(-k*age)) / k
 *      p   = p0 + v0*s + (g/k)*(age - s)
 *      v   = (v0 - g/k)*e^(-k*age) + g/k
 *
 * so a spark can decelerate hard, a cinder can arc and a smoke puff can coast
 * without a single CPU integration step. `uTime` is advanced with **realDt**
 * so bursts keep playing through hitstop, which is what sells the hit.
 *
 * Per-instance attributes
 *   iPos   vec3   spawn position (world)
 *   iVel   vec3   initial velocity (m/s)
 *   iA     vec4   birth, life, size at t=0, size at t=1
 *   iB     vec4   drag k, gravity, spin at t=0, spin rate
 *   iC     vec4   sprite cell, velocity stretch, turbulence, brightness
 *   iD     vec4   align mode, fade-in fraction, fade-out exponent, seed
 *   iCol0  ubyte4 rgb + alpha at t=0
 *   iCol1  ubyte4 rgb + alpha at t=1
 *
 * Align modes: 0 camera billboard, 1 velocity-stretched billboard,
 *              2 flat on the ground plane, 3 axial (quad hangs off the
 *              velocity vector and rolls to face the camera).
 */

/* Fill this scratch record and call `pool.push()`. It exists so emission is
 * allocation-free — no object literal is ever created at runtime. */
export const P = {
  x: 0, y: 0, z: 0,
  vx: 0, vy: 0, vz: 0,
  life: 1, size0: 1, size1: 1, sprite: 0,
  r0: 1, g0: 1, b0: 1, a0: 1,
  r1: 1, g1: 1, b1: 1, a1: 0,
  bright: 1, drag: 1.5, grav: 0,
  spin: 0, spinRate: 0, stretch: 0, turb: 0,
  align: 0, fadeIn: 0.05, curve: 1, delay: 0,
};

/** Reset the scratch record to sane defaults before filling it. */
export function resetP() {
  P.x = P.y = P.z = 0; P.vx = P.vy = P.vz = 0;
  P.life = 1; P.size0 = 1; P.size1 = 1; P.sprite = 0;
  P.r0 = P.g0 = P.b0 = 1; P.a0 = 1;
  P.r1 = P.g1 = P.b1 = 1; P.a1 = 0;
  P.bright = 1; P.drag = 1.5; P.grav = 0;
  P.spin = 0; P.spinRate = 0; P.stretch = 0; P.turb = 0;
  P.align = 0; P.fadeIn = 0.05; P.curve = 1; P.delay = 0;
  return P;
}

const VERT = /* glsl */`
attribute vec3 iPos;
attribute vec3 iVel;
attribute vec4 iA;
attribute vec4 iB;
attribute vec4 iC;
attribute vec4 iD;
attribute vec4 iCol0;
attribute vec4 iCol1;

uniform float uTime;
uniform vec3  uAtlas;     // 1/grid, grid, unused
uniform vec2  uFade;      // far fade start, far fade end
uniform float uScale;     // global size multiplier (quality)

varying vec2 vUv;
varying vec4 vCol;
varying float vView;

void main() {
  float age  = uTime - iA.x;
  float life = max(iA.y, 1e-4);
  float t    = age / life;
  if (age < 0.0 || t >= 1.0) {
    vUv = vec2(0.0); vCol = vec4(0.0); vView = 0.0;
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);   // behind the far plane: clipped
    return;
  }

  float k  = max(iB.x, 1e-3);
  float gy = iB.y;
  float e  = exp(-k * age);
  float s  = (1.0 - e) / k;

  vec3 p = iPos + iVel * s;
  p.y += gy * (age - s) / k;

  vec3 vel = (iVel - vec3(0.0, gy / k, 0.0)) * e + vec3(0.0, gy / k, 0.0);

  // cheap curl: three out-of-phase sinusoids, amplitude growing with age
  if (iC.z > 0.0) {
    float sd = iD.w;
    float ta = age * 2.1 + sd * 11.0;
    p += iC.z * age * vec3(sin(ta * 1.13 + sd * 6.2),
                           sin(ta * 0.79 + sd * 2.7) * 0.55,
                           cos(ta * 1.37 + sd * 9.1));
  }

  float sz  = mix(iA.z, iA.w, t) * uScale;
  float rot = iB.z + iB.w * age;

  vec2 q = position.xy;
  vec4 mv;
  int mode = int(iD.x + 0.5);

  if (mode == 2) {                                  // flat on the ground
    float c = cos(rot), sn = sin(rot);
    vec3 wq = vec3(q.x * c - q.y * sn, 0.0, q.x * sn + q.y * c) * sz;
    mv = viewMatrix * vec4(p + wq, 1.0);
  } else if (mode == 3) {                           // axial, rolls to camera
    vec3 dir = vel;
    float dl = length(dir);
    dir = dl > 1e-4 ? dir / dl : vec3(0.0, 1.0, 0.0);
    vec3 toCam = normalize(cameraPosition - p);
    vec3 side = cross(dir, toCam);
    float sl = length(side);
    side = sl > 1e-4 ? side / sl : vec3(1.0, 0.0, 0.0);
    float st = 1.0 + iC.y * dl;
    vec3 wq = dir * (q.y * sz * st) + side * (q.x * sz);
    mv = viewMatrix * vec4(p + wq, 1.0);
  } else {
    mv = viewMatrix * vec4(p, 1.0);
    if (mode == 1) {                                // stretch along screen vel
      vec3 vv = (viewMatrix * vec4(vel, 0.0)).xyz;
      vec2 ax = vv.xy;
      float l = length(ax);
      ax = l > 1e-4 ? ax / l : vec2(0.0, 1.0);
      vec2 pe = vec2(-ax.y, ax.x);
      float st = 1.0 + iC.y * length(vel);
      mv.xy += ax * (q.y * sz * st) + pe * (q.x * sz);
    } else {                                        // plain camera billboard
      float c = cos(rot), sn = sin(rot);
      mv.xy += vec2(q.x * c - q.y * sn, q.x * sn + q.y * c) * sz;
    }
  }

  float ft = pow(max(1.0 - t, 0.0), max(iD.z, 0.02));
  float fi = smoothstep(0.0, max(iD.y, 1e-4), t);
  float a  = mix(iCol0.a, iCol1.a, t) * ft * fi;

  vView = -mv.z;
  // never let a particle blow up in the camera's face, and let the far ones
  // dissolve into the aerial perspective instead of speckling the ridge
  a *= smoothstep(0.04, 0.55, vView) * smoothstep(uFade.y, uFade.x, vView);

  vCol = vec4(mix(iCol0.rgb, iCol1.rgb, t) * iC.w, a);

  vec2 cell = vec2(mod(iC.x, uAtlas.y), floor(iC.x * uAtlas.x));
  vUv = (uv * 0.94 + 0.03 + cell) * uAtlas.x;

  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = /* glsl */`
uniform sampler2D uMap;
uniform sampler2D uDepth;
uniform vec3 uSoft;          // amount, near, far  (amount 0 = off)
uniform vec2 uRes;
varying vec2 vUv;
varying vec4 vCol;
varying float vView;

void main() {
  vec4 t = texture2D(uMap, vUv);
  float a = t.a * vCol.a;
  if (a < 0.004) discard;
  if (uSoft.x > 0.0) {
    float d = texture2D(uDepth, gl_FragCoord.xy / uRes).x;
    float lz = (2.0 * uSoft.y * uSoft.z) / (uSoft.z + uSoft.y - (d * 2.0 - 1.0) * (uSoft.z - uSoft.y));
    a *= clamp((lz - vView) * uSoft.x, 0.0, 1.0);
  }
  gl_FragColor = vec4(vCol.rgb, a);
}
`;

const QUAD_POS = new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]);
const QUAD_UV = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);
const QUAD_IDX = new Uint16Array([0, 1, 2, 0, 2, 3]);

export class ParticlePool {
  /**
   * @param {number} max   slot count
   * @param {object} o     { additive, map, name, renderOrder }
   */
  constructor(max, o = {}) {
    this.max = Math.max(16, max | 0);
    const n = this.max;
    this.head = 0;          // next slot to hand out
    this.wrapped = false;   // the ring has been round at least once
    this.expiry = new Float32Array(n);
    this.priority = new Uint8Array(n);
    this.maxExpiry = -1;    // the whole pool is dead once now > maxExpiry
    this.spawned = 0;
    this.overwrites = 0;
    this.dropped = 0;
    this._dirtyLo = 0; this._dirtyHi = -1;

    this.aPos = new Float32Array(n * 3);
    this.aVel = new Float32Array(n * 3);
    this.aA = new Float32Array(n * 4);
    this.aB = new Float32Array(n * 4);
    this.aC = new Float32Array(n * 4);
    this.aD = new Float32Array(n * 4);
    this.aC0 = new Uint8Array(n * 4);
    this.aC1 = new Uint8Array(n * 4);

    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(QUAD_POS, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(QUAD_UV, 2));
    g.setIndex(new THREE.BufferAttribute(QUAD_IDX, 1));
    const dyn = (arr, size, norm = false, Ctor = THREE.InstancedBufferAttribute) => {
      const a = new Ctor(arr, size, norm);
      a.setUsage(THREE.DynamicDrawUsage);
      return a;
    };
    this._attrs = [
      g.setAttribute('iPos', dyn(this.aPos, 3)).attributes.iPos,
      g.setAttribute('iVel', dyn(this.aVel, 3)).attributes.iVel,
      g.setAttribute('iA', dyn(this.aA, 4)).attributes.iA,
      g.setAttribute('iB', dyn(this.aB, 4)).attributes.iB,
      g.setAttribute('iC', dyn(this.aC, 4)).attributes.iC,
      g.setAttribute('iD', dyn(this.aD, 4)).attributes.iD,
      g.setAttribute('iCol0', dyn(this.aC0, 4, true)).attributes.iCol0,
      g.setAttribute('iCol1', dyn(this.aC1, 4, true)).attributes.iCol1,
    ];
    // Two reusable range records per attribute — pushing preallocated objects
    // into `updateRanges` keeps partial uploads allocation-free.
    this._ranges = this._attrs.map(() => [{ start: 0, count: 0 }, { start: 0, count: 0 }]);
    g.instanceCount = 0;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.geometry = g;

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uAtlas: { value: new THREE.Vector3(1 / ATLAS_GRID, ATLAS_GRID, 0) },
        uFade: { value: new THREE.Vector2(150, 260) },
        uScale: { value: 1 },
        uMap: { value: o.map || null },
        uDepth: { value: null },
        uSoft: { value: new THREE.Vector3(0, 0.25, 900) },
        uRes: { value: new THREE.Vector2(1, 1) },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      blending: o.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      side: THREE.DoubleSide,
      toneMapped: false,
      fog: false,
    });
    this.material.name = o.name || 'vfxParticles';

    this.mesh = new THREE.Mesh(g, this.material);
    this.mesh.name = o.name || 'vfxParticles';
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.castShadow = this.mesh.receiveShadow = false;
    this.mesh.renderOrder = o.renderOrder ?? 10;
  }

  get live() {
    if (this.wrapped) return this.max;
    return this.head;
  }

  /**
   * Emit one particle from the scratch record `P`. Returns the slot or -1.
   * Low-priority ambience may not evict a still-live combat/hero slot. The
   * bounded probe keeps the hot path deterministic and allocation-free.
   */
  push(now, priority = 1) {
    const start = this.head;
    const n = this.max;
    let i = start;
    const pri = priority > 2 ? 2 : priority < 0 ? 0 : priority | 0;
    if (this.expiry[i] > now && this.priority[i] > pri) {
      i = -1;
      const probe = Math.min(24, n);
      for (let k = 1; k < probe; k++) {
        const j = (start + k) % n;
        if (this.expiry[j] <= now || this.priority[j] <= pri) { i = j; break; }
      }
      if (i < 0) { this.dropped++; return -1; }
    }
    if (this.expiry[i] > now) this.overwrites++;
    this.head = i + 1;
    if (this.head >= n) { this.head = 0; this.wrapped = true; }
    else if (i < start) this.wrapped = true;
    this.spawned++;
    this.priority[i] = pri;

    const i3 = i * 3, i4 = i * 4;
    const a = this.aPos; a[i3] = P.x; a[i3 + 1] = P.y; a[i3 + 2] = P.z;
    const v = this.aVel; v[i3] = P.vx; v[i3 + 1] = P.vy; v[i3 + 2] = P.vz;
    const A = this.aA;
    const birth = now + P.delay;
    A[i4] = birth; A[i4 + 1] = P.life; A[i4 + 2] = P.size0; A[i4 + 3] = P.size1;
    const B = this.aB;
    B[i4] = P.drag; B[i4 + 1] = P.grav; B[i4 + 2] = P.spin; B[i4 + 3] = P.spinRate;
    const C = this.aC;
    C[i4] = P.sprite; C[i4 + 1] = P.stretch; C[i4 + 2] = P.turb; C[i4 + 3] = P.bright;
    const D = this.aD;
    D[i4] = P.align; D[i4 + 1] = P.fadeIn; D[i4 + 2] = P.curve;
    D[i4 + 3] = (i * 0.6180339887) % 1;
    const c0 = this.aC0;
    c0[i4] = P.r0 * 255; c0[i4 + 1] = P.g0 * 255; c0[i4 + 2] = P.b0 * 255; c0[i4 + 3] = P.a0 * 255;
    const c1 = this.aC1;
    c1[i4] = P.r1 * 255; c1[i4 + 1] = P.g1 * 255; c1[i4 + 2] = P.b1 * 255; c1[i4 + 3] = P.a1 * 255;

    const exp = birth + P.life;
    this.expiry[i] = exp;
    if (exp > this.maxExpiry) this.maxExpiry = exp;
    if (this._dirtyHi < 0) { this._dirtyLo = i; this._dirtyHi = i; }
    else { if (i < this._dirtyLo) this._dirtyLo = i; if (i > this._dirtyHi) this._dirtyHi = i; }
    return i;
  }

  /** Upload whatever was written this frame and set the instance count. */
  flush(now) {
    this.material.uniforms.uTime.value = now;

    // When nothing is alive, rewind the ring so the draw shrinks back to zero.
    // O(1): the pool is empty exactly when the latest expiry has passed.
    if (this._dirtyHi < 0 && now > this.maxExpiry) {
      this.head = 0; this.wrapped = false;
    }

    if (this._dirtyHi >= 0) {
      const lo = this._dirtyLo, hi = this._dirtyHi, cnt = hi - lo + 1;
      for (let k = 0; k < this._attrs.length; k++) {
        const at = this._attrs[k], sz = at.itemSize, r = this._ranges[k];
        at.updateRanges.length = 0;
        r[0].start = lo * sz; r[0].count = cnt * sz;
        at.updateRanges.push(r[0]);
        at.needsUpdate = true;
      }
      this._dirtyLo = 0; this._dirtyHi = -1;
    }
    this.geometry.instanceCount = this.live;
    this.mesh.visible = this.live > 0;
  }

  /** How many slots still have time left on the clock. */
  count(now) {
    let c = 0;
    const n = this.wrapped ? this.max : this.head;
    for (let i = 0; i < n; i++) if (this.expiry[i] > now) c++;
    return c;
  }

  clear() {
    this.expiry.fill(-1);
    this.priority.fill(0);
    this.aA.fill(0);
    this.maxExpiry = -1;
    this.head = 0; this.wrapped = false;
    this.spawned = 0; this.overwrites = 0; this.dropped = 0;
    this._dirtyLo = 0; this._dirtyHi = this.max - 1;
  }

  dispose() { this.geometry.dispose(); this.material.dispose(); }
}
